<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ArrowRightIcon } from '@solar-icons/vue/linear'
import AppDialog from '@/components/primitives/AppDialog/AppDialog.vue'
import signature from '@/assets/brand/founder-signature.svg?raw'
import { FOUNDER_EMAIL, FOUNDER_FIRST_NAME, FOUNDER_NAME } from './founder-letter'

const props = defineProps<{
  /** Shows the letter. Only the "Start setup" button closes it - the caller flips this off. */
  open: boolean
  /** The signed-in user's first name; falls back to an anonymous greeting when absent. */
  firstName?: string
}>()

const emit = defineEmits<{
  start: []
}>()

const { t } = useI18n()

const mailto = computed(() => `mailto:${FOUNDER_EMAIL}`)
</script>

<template>
  <AppDialog
    :open="props.open"
    width="md"
    focus-actions
    :title="t('setup.founderLetter.title')"
    :confirm-label="t('setup.founderLetter.start')"
    :confirm-end-icon="ArrowRightIcon"
    hide-cancel
    @confirm="emit('start')"
  >
    <div class="flex flex-col gap-12">
      <div class="flex flex-col gap-8 text-body text-text-primary">
        <p>
          {{
            firstName
              ? t('setup.founderLetter.greeting', { name: firstName })
              : t('setup.founderLetter.greetingAnonymous')
          }}
        </p>

        <i18n-t keypath="setup.founderLetter.intro" tag="p" scope="global">
          <template #name>{{ FOUNDER_FIRST_NAME }}</template>
          <template #thanks>
            <strong class="text-body-emphasized">{{ t('setup.founderLetter.thanks') }}</strong>
          </template>
        </i18n-t>

        <p>{{ t('setup.founderLetter.story') }}</p>

        <i18n-t keypath="setup.founderLetter.plan" tag="p" scope="global">
          <template #time>
            <strong class="text-body-emphasized">{{ t('setup.founderLetter.time') }}</strong>
          </template>
          <template #care>
            <strong class="text-body-emphasized">{{ t('setup.founderLetter.care') }}</strong>
          </template>
        </i18n-t>

        <p>{{ t('setup.founderLetter.closing') }}</p>

        <span
          role="img"
          :aria-label="FOUNDER_NAME"
          class="block h-32 text-text-primary [&>svg]:h-full [&>svg]:w-auto"
          v-html="signature"
        />
      </div>

      <i18n-t keypath="setup.founderLetter.ps" tag="p" scope="global" class="text-body-sm">
        <template #label>
          <strong class="text-body-sm-emphasized">{{ t('setup.founderLetter.psLabel') }}</strong>
        </template>
        <template #email>
          <a class="text-accent-blue underline" :href="mailto">{{ FOUNDER_EMAIL }}</a>
        </template>
      </i18n-t>
    </div>
  </AppDialog>
</template>
